// The chat's model picker. The app sends only the tier with each question;
// the tender Lambda maps it to a Gemini model (terraform chat_models) and
// answers with Fast for a tier it does not know. Fast is today's model.
export type ChatModelTier = 'fast' | 'balanced' | 'thorough';

export interface ChatModelOption {
    tier: ChatModelTier;
    label: string;
    hint: string;
    cost: string;   // relative price per answer
}

export const CHAT_MODEL_OPTIONS: ChatModelOption[] = [
    { tier: 'fast',     label: 'Fast',     hint: 'Quick answers',                  cost: '$' },
    { tier: 'balanced', label: 'Balanced', hint: 'Better reasoning, a bit slower', cost: '$$' },
    { tier: 'thorough', label: 'Thorough', hint: 'Deepest analysis, slowest · expensive, use sparingly', cost: '$$$' },
];

export const DEFAULT_CHAT_MODEL_TIER: ChatModelTier = 'fast';

// Shown while Thorough is on. "About 8×" is gemini-3.1-pro-preview against
// gemini-3.1-flash-lite in llm_prices ($2 vs $0.25 input, $12 vs $1.50
// output per M tokens) — update it when those prices change.
export const THOROUGH_WARNING =
    'Thorough is on: about 8× the cost of Fast per answer. Use it sparingly, for questions that need deep analysis.';

export const chatModelOption = (tier: string | undefined): ChatModelOption | undefined =>
    CHAT_MODEL_OPTIONS.find((o) => o.tier === tier);

// Remembered in the browser: the tier picked last, for every tender.
const CHAT_MODEL_TIER_KEY = 'tenderq.chatModelTier';

export const loadChatModelTier = (): ChatModelTier => {
    try {
        const stored = chatModelOption(localStorage.getItem(CHAT_MODEL_TIER_KEY) ?? undefined);
        if (stored) return stored.tier;
    } catch { /* private mode: fall through to the default */ }
    return DEFAULT_CHAT_MODEL_TIER;
};

export const saveChatModelTier = (tier: ChatModelTier): void => {
    try { localStorage.setItem(CHAT_MODEL_TIER_KEY, tier); } catch { /* not remembered */ }
};

// USD, as Google bills and the Admin page shows. A tiny cost is not rounded
// down to $0.000, which would read as free.
export const formatCostUsd = (usd: number): string => {
    if (usd < 0.001) return '< $0.001';
    return `$${usd.toFixed(usd < 0.1 ? 3 : 2)}`;
};

// Label under an answer from a non-default tier; default answers keep their
// current look. costUsd reaches only admins (the backend sends it to Cognito
// groups admin and quantum), so its presence means admin mode.
export const answerModelLabel = (tier: string | undefined, costUsd: number | undefined): string | null => {
    const option = chatModelOption(tier);
    if (!option || option.tier === DEFAULT_CHAT_MODEL_TIER) return null;
    return costUsd === undefined
        ? `Answered with ${option.label}`
        : `Admin mode · ${option.label} · ${formatCostUsd(costUsd)}`;
};
